import { Host, h } from "@stencil/core";
import DOMPurify from "dompurify";
export class CoreLink {
    constructor() {
        this.label = undefined;
        this.href = undefined;
        this.linkTitle = undefined;
        this.variant = 'primary';
        this.size = 'medium';
        this.disabled = false;
        this.external = false;
        this.routerLink = false;
        this.iconName = null;
    }
    async onPropChange() {
        this.render();
    }
    /**
     * Sanitize href attribute to avoid XSS attacks
     */
    sanitizeHref() {
        return DOMPurify.sanitize(this.href, { USE_PROFILES: { html: true } });
    }
    getTextContentLabel() {
        return this.element.querySelector('a').textContent;
    }
    componentWillLoad() {
        this.routerLink && this.element.querySelector('a').parentElement.setAttribute('label', this.getTextContentLabel());
    }
    componentDidLoad() {
        this.element.querySelector('a').setAttribute('class', `core-anchor typography-body-${this.size}-bold`);
    }
    render() {
        return (h(Host, { key: '7f1a178db407ffa2427a9c0f8378042270de090f', class: `core-link core-link--${this.variant} core-link--${this.size} ${this.disabled ? 'core-link--disabled' : ''}` }, this.routerLink ? (h("slot", null)) : (h("a", Object.assign({ href: this.sanitizeHref(), title: this.linkTitle }, (this.external && {
            target: '_blank',
            rel: 'noopener noreferer'
        }), { "aria-disabled": this.disabled }), this.label, this.iconName && h("core-icon", { class: "core-link-icon", name: this.iconName, size: `number-size-icon-${this.size}` })))));
    }
    static get is() { return "core-link"; }
    static get originalStyleUrls() {
        return {
            "$": ["core-link.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["core-link.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This is the text of the link."
                },
                "attribute": "label",
                "reflect": false
            },
            "href": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The destination of the link."
                },
                "attribute": "href",
                "reflect": false
            },
            "linkTitle": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This is the title of the link. Please provide at list the name of the destination of the link."
                },
                "attribute": "link-title",
                "reflect": false
            },
            "variant": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'primary' | 'secondary'",
                    "resolved": "\"primary\" | \"secondary\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "A string indicating the design variation of the link."
                },
                "attribute": "variant",
                "reflect": false,
                "defaultValue": "'primary'"
            },
            "size": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'large' | 'medium' | 'small'",
                    "resolved": "\"large\" | \"medium\" | \"small\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "A string indicating the size variation of the link."
                },
                "attribute": "size",
                "reflect": false,
                "defaultValue": "'medium'"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating the disable state of the link. The `aria-disabled` attribute relies on this property."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "external": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating if the link is external or not.\nIf `true` it will open in a new tab."
                },
                "attribute": "external",
                "reflect": false,
                "defaultValue": "false"
            },
            "routerLink": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating if the component should behave as wrapper and allow to add child like Router Link."
                },
                "attribute": "router-link",
                "reflect": false,
                "defaultValue": "false"
            },
            "iconName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Extract<IconType, 'chevron_right-outline' | 'arrow_right-outline' | 'square_arrow_up_right-outline'>",
                    "resolved": "\"arrow_right-outline\" | \"chevron_right-outline\" | \"square_arrow_up_right-outline\"",
                    "references": {
                        "Extract": {
                            "location": "global",
                            "id": "global::Extract"
                        },
                        "IconType": {
                            "location": "import",
                            "path": "../core-icon/core-icon",
                            "id": "src/components/core-icon/core-icon.tsx::IconType"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Accept only `'chevron_right-outline' | 'arrow_right-outline' | 'square_arrow_up_right-outline'` from `@pollux-docaposte/icon-library`. By using the core-link component you don't have to install it as dependency."
                },
                "attribute": "icon-name",
                "reflect": false,
                "defaultValue": "null"
            }
        };
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "variant",
                "methodName": "onPropChange"
            }, {
                "propName": "size",
                "methodName": "onPropChange"
            }, {
                "propName": "disabled",
                "methodName": "onPropChange"
            }, {
                "propName": "iconName",
                "methodName": "onPropChange"
            }];
    }
}
//# sourceMappingURL=core-link.js.map
